import React, { useEffect, useState } from "react";
import { Plus, Trash2, ArrowUp, ArrowDown, Eye, EyeOff, Save } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

const uid = () => Math.random().toString(36).slice(2, 10);
const inputCls = "w-full bg-transparent border border-ink/15 rounded-md py-2 px-3 text-ink text-sm focus:outline-none focus:border-trailride transition-colors";

export function LinksTab() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/links-config").then((r) => setCfg(r.data)).catch(() => {});
  }, []);

  if (!cfg) return <p className="font-sans text-ink/40">Loading…</p>;

  const setField = (k) => (e) => setCfg({ ...cfg, [k]: e.target.value });
  const updateItem = (i, patch) => setCfg({ ...cfg, items: cfg.items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)) });
  const removeItem = (i) => setCfg({ ...cfg, items: cfg.items.filter((_, idx) => idx !== i) });
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= cfg.items.length) return;
    const copy = [...cfg.items];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    setCfg({ ...cfg, items: copy });
  };
  const addItem = (kind) => setCfg({ ...cfg, items: [...cfg.items, { id: uid(), kind, label: "", url: "", image_url: "", visible: true }] });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/links-config", cfg);
      toast.success("Links page saved");
    } catch {
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-links-tab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-serif text-2xl font-bold text-ink">Instagram links page</h2>
          <p className="font-sans text-sm text-ink/55 mt-1">Edit the /links page heading, tagline, background, and buttons.</p>
        </div>
        <div className="flex items-center gap-3">
          <a href="/links" target="_blank" rel="noreferrer" className="font-sans text-sm text-ink/60 hover:text-ink">Preview</a>
          <button onClick={save} disabled={saving} data-testid="links-save" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-trailride text-white font-sans text-sm font-medium hover:bg-trailride-dark disabled:opacity-60">
            <Save size={16} /> {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8 border border-ink/10 rounded-lg p-5">
        <div>
          <label className="font-sans text-xs text-ink/60">Heading</label>
          <input value={cfg.heading} onChange={setField("heading")} data-testid="links-heading" className={inputCls} />
        </div>
        <div>
          <label className="font-sans text-xs text-ink/60">Tagline</label>
          <input value={cfg.tagline} onChange={setField("tagline")} data-testid="links-tagline" className={inputCls} />
        </div>
        <div>
          <label className="font-sans text-xs text-ink/60">Background image URL</label>
          <input value={cfg.background_image} onChange={setField("background_image")} data-testid="links-bg" className={inputCls} />
        </div>
      </div>

      <div className="space-y-3">
        {cfg.items.map((it, i) => (
          <div key={it.id} className="border border-ink/15 rounded-md p-4" data-testid={`links-item-${i}`}>
            <div className="flex items-center justify-between mb-3">
              <span className="font-sans text-xs font-semibold uppercase tracking-wide text-trailride">{it.kind === "image" ? "Image" : "Link button"}</span>
              <div className="flex items-center gap-1">
                <button type="button" onClick={() => updateItem(i, { visible: !it.visible })} data-testid={`links-visible-${i}`} className="p-1.5 rounded hover:bg-ink/5 text-ink/60" title={it.visible ? "Hide" : "Show"}>{it.visible ? <Eye size={14} /> : <EyeOff size={14} />}</button>
                <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="p-1.5 rounded hover:bg-ink/5 disabled:opacity-30"><ArrowUp size={14} /></button>
                <button type="button" onClick={() => move(i, 1)} disabled={i === cfg.items.length - 1} className="p-1.5 rounded hover:bg-ink/5 disabled:opacity-30"><ArrowDown size={14} /></button>
                <button type="button" onClick={() => removeItem(i)} data-testid={`links-remove-${i}`} className="p-1.5 rounded hover:bg-red-50 text-red-500"><Trash2 size={14} /></button>
              </div>
            </div>
            {it.kind === "image" ? (
              <input value={it.image_url} onChange={(e) => updateItem(i, { image_url: e.target.value })} placeholder="Image URL" data-testid={`links-item-image-${i}`} className={inputCls} />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <input value={it.label} onChange={(e) => updateItem(i, { label: e.target.value })} placeholder="Button label" data-testid={`links-item-label-${i}`} className={inputCls} />
                <input value={it.url} onChange={(e) => updateItem(i, { url: e.target.value })} placeholder="URL (/events or https://…)" data-testid={`links-item-url-${i}`} className={inputCls} />
              </div>
            )}
            {!it.visible && <p className="font-sans text-xs text-ink/40 mt-2">Hidden on the live page</p>}
          </div>
        ))}
      </div>

      <div className="flex gap-2 mt-4">
        <button type="button" onClick={() => addItem("link")} data-testid="links-add-button" className="inline-flex items-center gap-1.5 font-sans text-xs font-medium border border-ink/20 rounded-full px-3 py-1.5 text-ink hover:border-trailride hover:text-trailride"><Plus size={13} /> Link button</button>
        <button type="button" onClick={() => addItem("image")} data-testid="links-add-image" className="inline-flex items-center gap-1.5 font-sans text-xs font-medium border border-ink/20 rounded-full px-3 py-1.5 text-ink hover:border-trailride hover:text-trailride"><Plus size={13} /> Image</button>
      </div>
    </div>
  );
}
